import { useProfile } from '@shared/hooks/useProfile';
import api from '@shared/services/api';
import { UserProfile } from '@shared/types/profile.types';
import { useQuery } from '@tanstack/react-query';

const CONNECTIONS_GC_TIME = 1000 * 60 * 5; // 5 minutes
const CONNECTIONS_STALE_TIME = 1000 * 60 * 1; // 1 minute

export interface ConnectionsData {
  connectedUsers: string[];
  userProfiles: Record<string, UserProfile>;
}

const fetchConnections = async (userId: string): Promise<ConnectionsData> => {
  const response = await api.get(`/connections/user/${userId}`);
  if (!response.data.success) {
    throw new Error('Failed to fetch connections');
  }

  const connections = response.data.connections.filter(
    (connection: any) => connection.connection.status === 'approved' && connection.id !== userId
  );

  // Create user profiles map and connected users array
  const userProfiles = connections.reduce((acc: Record<string, UserProfile>, connection: UserProfile) => {
    acc[connection.id] = connection;
    return acc;
  }, {});

  const connectedUsers = connections.map((connection: any) => connection.id);

  return {
    connectedUsers,
    userProfiles
  };
};

export const useConnections = () => {
  const { user } = useProfile();

  return useQuery({
    queryKey: ['connections', user?.id],
    queryFn: () => fetchConnections(user?.id || ''),
    enabled: !!user?.id,
    gcTime: CONNECTIONS_GC_TIME,
    staleTime: CONNECTIONS_STALE_TIME,
    // Return empty data if no user
    select: (data) => data || { connectedUsers: [], userProfiles: {} }
  });
};
